import { useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { KioskScreensaverTextDocument } from 'src/gql/graphql';
import { getLogger } from 'src/utils/datadog-logger';

const logger = getLogger('use-kiosk-screensaver-text');

/**
 * Dedicated, self-contained query for just the one field kiosks need to stay fresh on: the
 * Kiosk Pro screensaver text override. Kiosks are long-lived tabs, so this refetches on mount
 * and on window focus. Deliberately does NOT reuse useDispensary()/useQuery() (the shared,
 * heavily-consumed one-shot query hook) -- this hook's own Apollo useQuery + refetch() already
 * defaults to network-only on refetch, so no changes to shared infrastructure are needed, and
 * no other caller's behavior is affected.
 *
 * fetchPolicy: 'no-cache' -- this query's partial `orderTypesConfigV2 { kiosk { ... } }` shape
 * would otherwise write into the same normalized Dispensary:<id> entity that useDispensary()
 * populates fully, and Apollo has no merge policy configured for that type, so the partial
 * write would silently clobber every sibling field (delivery, pickup, etc.) already cached.
 *
 * Note: `filteredDispensaries` also has a server-side Redis cache (~60s TTL, see
 * apps/api/src/graphql/resolvers/dispensary/query-resolvers.js) that a client-side no-cache
 * fetch cannot bypass. This doesn't defeat the purpose here: Admin's updateOrderingSettings
 * mutation carries `@expireFragment(cacheTag: "dispensary-{id}", cacheGroup: "dispensary-list")`
 * (apps/api/src/graphql/resolvers/ordering-settings/type.js), so the one action that actually
 * changes screensaverText -- an Admin save -- always expires this cache immediately. The 60s
 * window only matters between unrelated cache-populating reads, not after a real edit.
 *
 * Return value distinguishes "not loaded yet" (undefined, caller should use its initial
 * fallback) from "loaded, and cleared" (empty string) -- collapsing both to undefined would
 * make a just-cleared screensaverText never win over a stale non-empty fallback.
 */
export function useKioskScreensaverText(dispensaryId: string | undefined): string | undefined {
  const { data, error, refetch } = useQuery(KioskScreensaverTextDocument, {
    variables: { dispensaryFilter: { cNameOrID: dispensaryId } },
    skip: !dispensaryId,
    fetchPolicy: 'no-cache',
  });

  // The mount-time refresh() call below runs in the same effect-processing pass as useQuery's
  // own automatic initial fetch, so both requests are in-flight simultaneously for the same
  // query/variables -- Apollo's default queryDeduplication (true) coalesces them into a single
  // actual network call. No extra guard needed; this only actually issues a second network
  // request when the window regains focus, well after the initial fetch has settled.
  useEffect(() => {
    if (!dispensaryId) {
      return undefined;
    }

    const refresh = (): void => {
      refetch().catch((refetchError: unknown) => {
        logger.error('kiosk_screensaver_text_refetch_failed', { dispensaryId, error: refetchError });
      });
    };

    refresh();

    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [dispensaryId, refetch]);

  if (error) {
    logger.error('kiosk_screensaver_text_query_failed', { dispensaryId, error });
  }

  // An empty array means the query resolved but found no matching dispensary -- not the same
  // as a dispensary that resolved with a cleared screensaverText. Treat both as "not loaded"
  // (undefined) rather than accidentally returning '' and overriding the caller's fallback.
  const matchedDispensary = data?.filteredDispensaries?.[0];
  if (!matchedDispensary) {
    return undefined;
  }

  return matchedDispensary.orderTypesConfigV2?.kiosk?.screensaverText ?? '';
}
